import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { fetchCalendarMonth } from '../../services/calendarService';
import { buildFlyerUrl } from '../../services/flyerService';
import { getCalendarEventTypeClass } from '../../utils/tournamentCalendar';
import { logError } from '../../utils/errorHandlers';
import { buildEventJsonLd, clearJsonLd, injectJsonLd } from '../../utils/seo';

const DAYS_OF_WEEK = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const MONTH_FORMATTER = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' });
const LONG_DATE_FORMATTER = new Intl.DateTimeFormat('de-DE', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});
const TIME_FORMATTER = new Intl.DateTimeFormat('de-DE', {
  hour: '2-digit',
  minute: '2-digit',
});

const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const createEventId = (event) => {
  if (event.id) {
    return `calendar-event-${event.id}`;
  }
  return `calendar-event-${event.title || 'unknown'}-${event.start || ''}-${event.end || ''}`;
};

const sortEventsByStart = (eventList = []) => {
  return [...eventList].sort((a, b) => {
    const aStart = new Date(a.start || 0).getTime();
    const bStart = new Date(b.start || 0).getTime();
    return aStart - bStart;
  });
};

const buildMonthWeeks = (year, month) => {
  const firstDayOfMonth = new Date(year, month - 1, 1);
  const totalDaysInMonth = new Date(year, month, 0).getDate();
  const startWeekDay = (firstDayOfMonth.getDay() + 6) % 7; // convert so Monday = 0

  const slots = [];

  for (let i = 0; i < startWeekDay; i += 1) {
    slots.push({ dayNumber: null, isCurrentMonth: false, date: null });
  }

  for (let day = 1; day <= totalDaysInMonth; day += 1) {
    const date = new Date(year, month - 1, day);
    slots.push({ dayNumber: day, isCurrentMonth: true, date });
  }

  while (slots.length % 7 !== 0) {
    slots.push({ dayNumber: null, isCurrentMonth: false, date: null });
  }

  while (slots.length < 42) {
    slots.push({ dayNumber: null, isCurrentMonth: false, date: null });
  }

  const weeks = [];
  for (let i = 0; i < slots.length; i += 7) {
    weeks.push(slots.slice(i, i + 7));
  }
  return weeks;
};

function trimTrailingEmptyWeeks(weeks) {
  let end = weeks.length;
  while (end > 0) {
    const week = weeks[end - 1];
    const allEmpty = week.every((slot) => !slot.isCurrentMonth);
    if (!allEmpty) break;
    end -= 1;
  }
  return weeks.slice(0, end);
}

const getIsMobile = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.innerWidth < 900;
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(getIsMobile);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => {};
    }

    const handleResize = () => setIsMobile(getIsMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

const formatEventTimeRange = (event) => {
  if (!event?.start) {
    return '';
  }

  const startDate = new Date(event.start);
  const endDate = event.end ? new Date(event.end) : null;

  if (endDate) {
    return `${TIME_FORMATTER.format(startDate)} – ${TIME_FORMATTER.format(endDate)}`;
  }

  return TIME_FORMATTER.format(startDate);
};

const compareMonthOnly = (dateA, dateB) => {
  const yearDiff = dateA.getFullYear() - dateB.getFullYear();
  if (yearDiff !== 0) {
    return yearDiff;
  }
  return dateA.getMonth() - dateB.getMonth();
};

const parseDateKey = (key) => {
  const [year, month, day] = key.split('-').map((value) => parseInt(value, 10));
  return new Date(year, month - 1, day);
};

export const CalendarView = () => {
  const today = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);

  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [expandedEventId, setExpandedEventId] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const isMobile = useIsMobile();

  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isSwipeAnimating, setIsSwipeAnimating] = useState(false);
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const isSwipingRef = useRef(false);
  const currentSwipeOffsetRef = useRef(0);

  const minMonthDate = useMemo(() => new Date(today.getFullYear(), today.getMonth() - 3, 1), [today]);
  const maxMonthDate = useMemo(() => new Date(today.getFullYear(), today.getMonth() + 6, 1), [today]);
  const currentMonthDate = useMemo(() => new Date(currentYear, currentMonth - 1, 1), [currentYear, currentMonth]);

  const canGoPrev = compareMonthOnly(currentMonthDate, minMonthDate) > 0;
  const canGoNext = compareMonthOnly(currentMonthDate, maxMonthDate) < 0;
  const isCurrentMonth = currentMonthDate.getFullYear() === today.getFullYear() && currentMonthDate.getMonth() === today.getMonth();

  useEffect(() => {
    let isSubscribed = true;

    const loadCalendar = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetchCalendarMonth({ year: currentYear, month: currentMonth });
        if (!isSubscribed) {
          return;
        }
        setEvents(sortEventsByStart(response.events || []));
        setSelectedEvent(null);
        setExpandedEventId(null);
        setLoading(false);
      } catch (serviceError) {
        if (!isSubscribed) {
          return;
        }
        logError('[CalendarView] Failed to load calendar month', serviceError);
        setEvents([]);
        setError('Kalender konnte nicht geladen werden. Bitte versuche es später erneut.');
        setSelectedEvent(null);
        setExpandedEventId(null);
        setLoading(false);
      }
    };

    loadCalendar();

    return () => {
      isSubscribed = false;
    };
  }, [currentYear, currentMonth, retryCount]);

  useEffect(() => {
    if (events.length > 0) {
      injectJsonLd(buildEventJsonLd(events), 'events-jsonld');
    } else {
      clearJsonLd('events-jsonld');
    }
    return () => clearJsonLd('events-jsonld');
  }, [events]);

  const eventsByDay = useMemo(() => {
    const grouping = {};
    sortEventsByStart(events).forEach((event) => {
      if (!event?.start) {
        return;
      }
      const dateKey = formatDateKey(new Date(event.start));
      if (!grouping[dateKey]) {
        grouping[dateKey] = [];
      }
      grouping[dateKey].push(event);
    });
    return grouping;
  }, [events]);

  const weeks = useMemo(() => buildMonthWeeks(currentYear, currentMonth), [currentYear, currentMonth]);
  const visibleWeeks = useMemo(() => trimTrailingEmptyWeeks(weeks), [weeks]);

  const mobileEventGroups = useMemo(() => {
    const groups = [];
    Object.entries(eventsByDay).forEach(([dateKey, groupedEvents]) => {
      const date = parseDateKey(dateKey);
      if (date.getFullYear() === currentYear && date.getMonth() + 1 === currentMonth) {
        groups.push({ date, events: groupedEvents });
      }
    });
    return groups.sort((a, b) => a.date - b.date);
  }, [eventsByDay, currentYear, currentMonth]);

  const monthLabel = MONTH_FORMATTER.format(currentMonthDate);

  const handleChangeMonth = (delta) => {
    if (delta < 0 && !canGoPrev) {
      return;
    }
    if (delta > 0 && !canGoNext) {
      return;
    }
    const newDate = new Date(currentYear, currentMonth - 1 + delta, 1);
    setCurrentYear(newDate.getFullYear());
    setCurrentMonth(newDate.getMonth() + 1);
  };

  const handleSelectToday = () => {
    if (isCurrentMonth) {
      return;
    }
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth() + 1);
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    if (isMobile) {
      const eventId = createEventId(event);
      setExpandedEventId((current) => (current === eventId ? null : eventId));
    }
  };

  const handleCloseOverlay = () => {
    setSelectedEvent(null);
  };

  const handleRetry = () => {
    setRetryCount((c) => c + 1);
  };

  const bounceBack = () => {
    setIsDragging(false);
    setSwipeOffset(0);
    currentSwipeOffsetRef.current = 0;
    isSwipingRef.current = false;
  };

  const animateSwipeTransition = (monthDelta) => {
    const viewportWidth = window.innerWidth;
    const exitOffset = monthDelta > 0 ? -viewportWidth : viewportWidth;

    setIsDragging(false);
    setIsSwipeAnimating(true);

    setSwipeOffset(exitOffset);
    currentSwipeOffsetRef.current = exitOffset;

    setTimeout(() => {
      handleChangeMonth(monthDelta);
      setSwipeOffset(-exitOffset);
      currentSwipeOffsetRef.current = -exitOffset;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setSwipeOffset(0);
          currentSwipeOffsetRef.current = 0;

          setTimeout(() => {
            setIsSwipeAnimating(false);
          }, 350);
        });
      });
    }, 250);
  };

  const handleTouchStart = (e) => {
    if (isSwipeAnimating) {
      return;
    }
    const touch = e.touches[0];
    if (!touch) {
      return;
    }
    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
  };

  const handleTouchMove = (e) => {
    if (isSwipeAnimating) {
      return;
    }

    const touch = e.touches[0];
    if (!touch) {
      return;
    }

    const deltaX = touch.clientX - touchStartXRef.current;
    const deltaY = touch.clientY - touchStartYRef.current;

    if (!isSwipingRef.current) {
      if (Math.abs(deltaX) < 10) {
        return;
      }
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        isSwipingRef.current = true;
        setIsDragging(true);
      } else {
        return;
      }
    }

    e.preventDefault();

    let clampedDelta = deltaX;
    const RUBBER_BAND_LIMIT = 50;
    if (deltaX > 0 && !canGoPrev) {
      clampedDelta = Math.min(deltaX, RUBBER_BAND_LIMIT);
    } else if (deltaX < 0 && !canGoNext) {
      clampedDelta = Math.max(deltaX, -RUBBER_BAND_LIMIT);
    }

    currentSwipeOffsetRef.current = clampedDelta;
    setSwipeOffset(clampedDelta);
  };

  const handleTouchEnd = () => {
    if (isSwipeAnimating || !isSwipingRef.current) {
      return;
    }
    isSwipingRef.current = false;

    const SWIPE_THRESHOLD = 60;
    const deltaX = currentSwipeOffsetRef.current;

    if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
      if (deltaX > 0 && canGoPrev) {
        animateSwipeTransition(-1);
        return;
      }
      if (deltaX < 0 && canGoNext) {
        animateSwipeTransition(1);
        return;
      }
    }

    bounceBack();
  };

  const handleTouchCancel = () => {
    isSwipingRef.current = false;
    setIsDragging(false);
    setSwipeOffset(0);
    currentSwipeOffsetRef.current = 0;
  };

  const FlyerBlock = ({ flyerUrl, title }) => {
    if (!flyerUrl) {
      return null;
    }

    return (
      <div className="calendar-flyer">
        <a
          href={flyerUrl}
          download
          target="_blank"
          rel="noreferrer"
          className="calendar-flyer-link"
        >
          Flyer zum Teilen
        </a>
        <img src={flyerUrl} alt={`Flyer ${title}`} loading="lazy" />
      </div>
    );
  };

  const renderDesktopGrid = () => (
    <div className="calendar-grid" data-testid="calendar-grid">
      <div className="calendar-grid-header">
        {DAYS_OF_WEEK.map((day) => (
          <div key={day} className="calendar-grid-header-cell">
            {day}
          </div>
        ))}
      </div>
      <div className="calendar-grid-body" data-testid="calendar-grid-body">
        {visibleWeeks.map((week, weekIndex) =>
          week.map((slot, slotIndex) => {
            const slotKey = slot.date ? formatDateKey(slot.date) : `placeholder-${weekIndex}-${slotIndex}`;
            if (!slot.isCurrentMonth || !slot.date) {
              return (
                <div
                  key={slotKey}
                  className="calendar-day calendar-day--empty"
                  data-testid="calendar-day-empty"
                  aria-hidden="true"
                />
              );
            }

            const dateKey = formatDateKey(slot.date);
            const dailyEvents = (eventsByDay[dateKey] || []).filter(
              (event) => formatDateKey(new Date(event.start)) === dateKey,
            );

            return (
              <div key={slotKey} className="calendar-day" data-testid="calendar-day">
                <div className="calendar-day-number">{slot.dayNumber}</div>
                <div className="calendar-events">
                  {dailyEvents.map((event) => {
                    const eventId = createEventId(event);
                    const typeClass = getCalendarEventTypeClass(event);
                    const eventClassName = `calendar-event${typeClass ? ` calendar-event--${typeClass}` : ''}`;
                    return (
                      <button
                        key={eventId}
                        type="button"
                        className={eventClassName}
                        onClick={() => handleEventClick(event)}
                        data-testid={`calendar-event-grid-${eventId}`}
                      >
                        <span className="calendar-event-time">{formatEventTimeRange(event)}</span>
                        <span className="calendar-event-title">{event.title}</span>
                        {event.isRegistrationActive && (
                          <span className="calendar-badge calendar-badge--registration" data-testid={`calendar-registration-badge-${eventId}`}>
                            Anmeldung offen
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  const renderMobileList = () => (
    <div className="calendar-list-mobile" data-testid="calendar-mobile-list">
      {mobileEventGroups.map(({ date, events: groupedEvents }) => {
        const heading = LONG_DATE_FORMATTER.format(date);
        return (
          <div key={formatDateKey(date)} className="calendar-list-group">
            <div className="calendar-list-date">{heading}</div>
            <ul className="calendar-list-events">
              {groupedEvents.map((event) => {
                const eventId = createEventId(event);
                const isExpanded = expandedEventId === eventId;
                const flyerUrl = event.flyerId ? buildFlyerUrl({ flyerId: event.flyerId }) : null;
                const typeClass = getCalendarEventTypeClass(event);
                const eventClassName = `calendar-event list${typeClass ? ` calendar-event--${typeClass}` : ''}`;
                return (
                  <li key={eventId} className={`calendar-list-event ${isExpanded ? 'is-expanded' : ''}`}>
                    <button
                      type="button"
                      className={eventClassName}
                      onClick={() => handleEventClick(event)}
                      aria-expanded={isExpanded}
                      data-testid={`calendar-event-list-${eventId}`}
                    >
                      <div className="calendar-event-main">
                        <span className="calendar-event-time">{formatEventTimeRange(event)}</span>
                        <span className="calendar-event-title">{event.title}</span>
                        {event.isRegistrationActive && (
                          <span className="calendar-badge calendar-badge--registration" data-testid={`calendar-registration-badge-${eventId}`}>
                            Anmeldung offen
                          </span>
                        )}
                      </div>
                      <span className="calendar-event-chevron" aria-hidden="true">▾</span>
                    </button>
                    <div
                      className={`calendar-event-detail ${isExpanded ? 'is-visible' : ''}`}
                      data-testid={`calendar-event-detail-${eventId}`}
                    >
                      <div className="calendar-event-detail-row">
                        <strong>Zeit:</strong>
                        <span>{formatEventTimeRange(event)}</span>
                      </div>
                      {event.location && (
                        <div className="calendar-event-detail-row">
                          <strong>Ort:</strong>
                          <span>{event.location}</span>
                        </div>
                      )}
                      {event.description && (
                        <div className="calendar-event-detail-row">
                          <strong>Beschreibung:</strong>
                          <span>{event.description}</span>
                        </div>
                      )}
                      {event.isRegistrationActive && event.tournamentId && (
                        <div className="calendar-event-detail-row calendar-event-detail-row--cta">
                          <Link
                            to={`/challenger?tournamentId=${encodeURIComponent(event.tournamentId)}`}
                            className="calendar-registration-link"
                            data-testid={`calendar-registration-link-mobile-${eventId}`}
                          >
                            Zur Anmeldung
                          </Link>
                        </div>
                      )}
                      <FlyerBlock flyerUrl={flyerUrl} title={event.title} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );

  const renderOverlay = () => {
    if (!selectedEvent || isMobile) {
      return null;
    }

    const flyerUrl = selectedEvent.flyerId ? buildFlyerUrl({ flyerId: selectedEvent.flyerId }) : null;
    const typeClass = getCalendarEventTypeClass(selectedEvent);
    const cardClassName = `calendar-event-detail-card${typeClass ? ` calendar-event-detail-card--${typeClass}` : ''}`;

    return (
      <div className="calendar-event-overlay" data-testid="calendar-event-overlay">
        <div className={cardClassName} data-testid="calendar-event-detail-card">
          <button type="button" className="calendar-event-close" onClick={handleCloseOverlay} aria-label="Detail schließen">
            ×
          </button>
          <h4>{selectedEvent.title}</h4>
          <p className="calendar-event-detail-date">
            {selectedEvent.start && LONG_DATE_FORMATTER.format(new Date(selectedEvent.start))}
          </p>
          <p className="calendar-event-detail-time">{formatEventTimeRange(selectedEvent)}</p>
          {selectedEvent.location && (
            <p className="calendar-event-detail-location">
              <strong>Ort:</strong> {selectedEvent.location}
            </p>
          )}
          {selectedEvent.description && (
            <p className="calendar-event-detail-description">{selectedEvent.description}</p>
          )}
          {selectedEvent.isRegistrationActive && selectedEvent.tournamentId && (
            <div className="calendar-event-detail-cta">
              <Link
                to={`/challenger?tournamentId=${encodeURIComponent(selectedEvent.tournamentId)}`}
                className="calendar-registration-link"
                data-testid="calendar-registration-link-overlay"
              >
                Zur Anmeldung
              </Link>
            </div>
          )}
          <FlyerBlock flyerUrl={flyerUrl} title={selectedEvent.title} />
        </div>
      </div>
    );
  };

  const swipeContainerStyle = useMemo(() => {
    if (!isMobile) {
      return {};
    }
    const style = {};
    if (!isDragging && swipeOffset !== 0) {
      style.transition = 'transform 0.3s ease, opacity 0.3s ease';
    }
    if (swipeOffset !== 0) {
      style.transform = `translateX(${swipeOffset}px)`;
      style.opacity = Math.max(0, 1 - Math.abs(swipeOffset) / (window.innerWidth * 0.8));
    }
    return style;
  }, [isMobile, isDragging, swipeOffset]);

  return (
    <section className="calendar-view" data-testid="calendar-view">
      <div className="calendar-header">
        <button
          type="button"
          className="calendar-nav-btn"
          onClick={() => handleChangeMonth(-1)}
          disabled={!canGoPrev}
          aria-label="Vorheriger Monat"
          data-testid="calendar-prev-button"
        >
          ‹
        </button>
        <div className="calendar-current">
          <div className="calendar-current-month">{monthLabel}</div>
          <button
            type="button"
            className="calendar-today-btn"
            onClick={handleSelectToday}
            disabled={isCurrentMonth}
            aria-label="Heute"
            data-testid="calendar-today-button"
          >
            Heute
          </button>
        </div>
        <button
          type="button"
          className="calendar-nav-btn"
          onClick={() => handleChangeMonth(1)}
          disabled={!canGoNext}
          aria-label="Nächster Monat"
          data-testid="calendar-next-button"
        >
          ›
        </button>
      </div>

      {error && (
        <div className="calendar-error-box" role="alert" data-testid="calendar-error-message">
          <span className="calendar-error-icon">⚠</span>
          <p>{error}</p>
          <button type="button" className="calendar-retry-btn" onClick={handleRetry}>
            Erneut versuchen
          </button>
        </div>
      )}
      <div
        className="calendar-swipe-container"
        style={swipeContainerStyle}
        onTouchStart={isMobile ? handleTouchStart : undefined}
        onTouchMove={isMobile ? handleTouchMove : undefined}
        onTouchEnd={isMobile ? handleTouchEnd : undefined}
        onTouchCancel={isMobile ? handleTouchCancel : undefined}
        data-testid="calendar-swipe-container"
      >
        {loading && !isSwipeAnimating ? (
          <div className="calendar-loading">
            <div className="calendar-loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <p>Kalender wird geladen …</p>
          </div>
        ) : (
          <>
            {renderDesktopGrid()}
            {renderMobileList()}
            {!error && events.length === 0 && (
              <div className="calendar-empty-box" data-testid="calendar-empty-message">
                <span className="calendar-empty-icon">📅</span>
                <p>Keine Termine für diesen Monat.</p>
              </div>
            )}
          </>
        )}
      </div>
      {renderOverlay()}
    </section>
  );
};

export default CalendarView;
